import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { CSSTransition } from 'react-transition-group';
import cx from 'classnames';
import { NavOrExternalLink } from 'src/client/components/ui/NavOrExternalLink';
import styled from 'styled-components/macro';
import { DESKTOP, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';

type CategoryLink = {
  className?: string;
  isExternal?: boolean;
  label: string;
  openNewWindow?: boolean;
  to: string;
};

type Props = {
  id: string;
  label: string;
  links: CategoryLink[];
  openedCategories: string[];
  setOpenCategories: (categories: string[]) => void;
};

export const FooterCategoryMenu = (props: Props) => {
  const { id, label, links, openedCategories, setOpenCategories } = props;
  const isOpen = openedCategories.includes(id);

  const toggleCategorySection = () => {
    if (isOpen) {
      setOpenCategories(openedCategories.filter((cat) => cat !== id));
      return;
    }
    setOpenCategories([...openedCategories, id]);
  };

  const items = links.map((item) => {
    return (
      <MenuLinkWrapper className={cx('link', item.className)} key={`${id}-${item.to}`}>
        <NavOrExternalLink to={item.to} openNewWindow={item.openNewWindow || item.isExternal}>
          {item.label}
        </NavOrExternalLink>
      </MenuLinkWrapper>
    );
  });

  return (
    <MenuListWrapper key={id}>
      <div onClick={toggleCategorySection} data-group={id}>
        <MenuTitle className={cx({ active: isOpen })}>
          {label}
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="pull-right visible-xs" />
        </MenuTitle>
      </div>
      <CSSTransition classNames="inner-menu" in={isOpen} timeout={250}>
        <InnerMenuList>{items}</InnerMenuList>
      </CSSTransition>
    </MenuListWrapper>
  );
};

export const MenuListWrapper = styled.li`
  display: flex;
  flex-direction: column;
  width: 100%;
  list-style: none;
  border-top: 1px solid ${(props) => props.theme.footerBarPrimary4};

  @media ${TABLET_PORTRAIT} {
    border-top: 0;
  }

  @media ${DESKTOP} {
    max-width: 190px;
  }
`;

export const MenuTitle = styled.span`
  display: block;
  color: ${(props) => props.theme.footerBarPrimary3};
  cursor: pointer;
  padding: 22px 24px;
  text-transform: uppercase;
  text-align: center;
  font-size: 1rem;
  line-height: 14px;

  &.active {
    background: ${(props) => props.theme.footerBarPrimary2};
    color: ${(props) => props.theme.footerBarPrimary5};
  }

  &:last-child {
    border-bottom-color: ${(props) => props.theme.footerBarPrimary4};
  }

  @media ${TABLET_PORTRAIT} {
    background: none;
    color: ${(props) => props.theme.footerBarPrimary3};
    line-height: 1.4;
    text-align: left;
    cursor: default;
    margin-bottom: 12px;
    padding: 0 0 12px;
    border-bottom: 1px solid ${(props) => props.theme.footerBarPrimary4};
    transition: background-color 0.3s;
    white-space: nowrap;
  }
`;

export const MenuLinkWrapper = styled.li`
  width: 100%;

  a {
    display: block;
    color: ${(props) => props.theme.footerBarPrimary2};
    background: ${(props) => props.theme.footerBarPrimary5};
    border-bottom: 1px solid ${(props) => props.theme.footerBarPrimary4};
    cursor: pointer;
    line-height: 14px;
    margin-bottom: 0;
    padding: 22px 24px;
    font-size: 1rem;
    text-align: center;
    text-transform: uppercase;

    @media ${TABLET_PORTRAIT} {
      text-transform: none;
      text-align: left;
      line-height: 1.4;
      font-size: 0.9rem;
      text-decoration: none;
      display: block;
      padding: 0;
      color: ${(props) => props.theme.footerBarPrimary2};
      background: none;
      border: none;
    }
  }

  a:hover {
    color: ${(props) => props.theme.footerBarPrimary3};
  }

  a:not(.btn) {
    color: ${(props) => props.theme.footerBarPrimary2};
  }

  a:not(.btn):hover {
    color: ${(props) => props.theme.footerBarPrimary3};
  }

  &.standout a {
    color: ${(props) => props.theme.footerbarHighlight};

    &:hover {
      color: ${(props) => props.theme.footerBarPrimary3};
    }
  }
`;

const InnerMenuList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  list-style: none;
  padding: 0;
  align-items: flex-start;
  overflow: hidden;
  max-height: 0;
  transition: max-height 250ms ease-in-out;

  &.inner-menu-enter.inner-menu-enter-active,
  &.inner-menu-enter-done,
  &.inner-menu-exit {
    max-height: 350px;
  }

  &.inner-menu-exit.inner-menu-exit-active {
    max-height: 0;
  }

  @media ${TABLET_PORTRAIT} {
    max-height: none;
    gap: 10px;
  }
`;
